import React from "react";
import { formatDualCurrency } from "../utils/currency";

/**
 * Calculate total price with fees
 * @param {number} basePrice - Base program price
 * @param {number} platformFeePercent - Platform fee percentage (default 9%)
 * @param {number} salesTaxPercent - Sales tax percentage (default 10%)
 * @returns {object} - { basePrice, platformFee, subtotal, salesTax, total }
 */
export const calculateTotalWithFees = (basePrice, platformFeePercent = 9, salesTaxPercent = 10) => {
  const platformFee = Math.round(basePrice * (platformFeePercent / 100));
  const subtotal = basePrice + platformFee;
  const salesTax = Math.round(subtotal * (salesTaxPercent / 100));
  const total = subtotal + salesTax;
  return { basePrice, platformFee, subtotal, salesTax, total };
};

/**
 * Component to display prices in both JPY and USD
 * Primary currency (JPY) is shown larger, USD is shown smaller underneath
 */
const PriceDisplay = ({ 
  price, 
  currency = "JPY", 
  showUSD = true,
  showWithFees = false,
  size = "md",
  className = "" 
}) => {
  // Calculate total with fees if requested
  const displayPrice = showWithFees ? calculateTotalWithFees(price).total : price;

  // If price is in USD, dont show conversion for now
  if (currency === "USD") {
    return (
      <div className={className}>
        <span className={getSizeClass(size)}>${displayPrice.toFixed(2)}</span>
        {showWithFees && (
          <span className="text-xs text-slate-500 ml-1">(incl. fees)</span>
        )}
      </div>
    );
  }

  // Default: Price is in JPY
  const { jpy, usd } = formatDualCurrency(displayPrice, showUSD);

  return (
    <div className={`${className} flex flex-col`}>
      <span className={`font-bold ${getSizeClass(size)}`}>
        {jpy}
        {showWithFees && (
          <span className="text-xs font-normal text-slate-500 ml-1">(incl. fees & tax)</span>
        )}
      </span>
      {showUSD && (
        <span className={`text-slate-500 ${getUSDSizeClass(size)}`}>
          ~{usd} USD
        </span>
      )}
    </div>
  );
};

/**
 * Get text size class based on size prop
 */
const getSizeClass = (size) => {
  const sizes = {
    "sm": "text-sm",
    "md": "text-lg",
    "lg": "text-2xl",
    "xl": "text-3xl"
  };
  return sizes[size] || sizes["md"];
};

/**
 * Get USD text size class (always smaller than JPY)
 */
const getUSDSizeClass = (size) => {
  const sizes = {
    "sm": "text-xs",
    "md": "text-sm",
    "lg": "text-base",
    "xl": "text-lg"
  };
  return sizes[size] || sizes["md"];
};

export default PriceDisplay;
